import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../styles/CaseStory.css";

function FadeIn({ children, delay = 0 }) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setV(true);
      },
      { threshold: 0.1 },
    );
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{
        opacity: v ? 1 : 0,
        transform: v ? "translateY(0)" : "translateY(20px)",
        transition: `all .6s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

const CaseStoryGrid = () => {
  const [selectedService, setSelectedService] = useState("all");

  const caseStudies = [
    {
      id: 1,
      slug: "shopify-conversion-rate-optimization-how-we-increased-sales-without-more-ads",
      service: "Shopify Development",
      category: "CRO & Optimization",
      title: "Shopify Conversion Rate Optimization",
      client: "DTC Brand",
      industry: "Premium Beauty",
      metrics: [
        { label: "Revenue Surge", value: "289%" },
        { label: "Conversion", value: "3.8%" },
        { label: "Less Abandon", value: "54%" },
      ],
      tags: ["Shopify", "CRO", "A/B Testing"],
      image:
        "https://cdn.shopify.com/s/files/1/0644/4086/0781/files/Lumene-Label-photo.webp?v=1771915234&width=600",
    },
    {
      id: 2,
      slug: "how-a-sales-funnel-tripled-our-clients-revenue",
      service: "Shopify Development",
      category: "Sales Funnel",
      title: "How a Sales Funnel Tripled Our Client's Revenue",
      client: "Driftline Co.",
      industry: "Fashion & Lifestyle",
      metrics: [
        { label: "Revenue Surge", value: "289%" },
        { label: "Conversion", value: "3.8%" },
        { label: "Less Abandon", value: "54%" },
      ],
      tags: ["Shopify", "Funnels", "CRO"],
      image:
        "https://cdn.shopify.com/s/files/1/0644/4086/0781/files/Contemporary_Streetwear_Brand.webp?v=1771915524&width=600",
    },
    {
      id: 3,
      slug: "shopify-store-redesign-case-study-2-to-4-percent-conversion-rate",
      service: "Shopify Development",
      category: "Store Redesign",
      title: "From 2% to 4%: Shopify Store Redesign",
      client: "ArcVault Jewelry",
      industry: "Jewelry & Accessories",
      metrics: [
        { label: "Conversion", value: "4.6%" },
        { label: "Revenue Lift", value: "341%" },
        { label: "AOV Increase", value: "67%" },
      ],
      tags: ["Shopify", "Redesign", "Luxury"],
      image:
        "https://cdn.shopify.com/s/files/1/0644/4086/0781/files/ArcVault.webp?v=1771919829&width=600",
    },
    {
      id: 4,
      slug: "how-a-7-figure-brand-added-620k-in-revenue",
      service: "Shopify Development",
      category: "CRO & Optimization",
      title: "How a 7-Figure Brand Added $620K in Revenue",
      client: "Lumisoft Beauty",
      industry: "Premium Beauty",
      metrics: [
        { label: "Extra Revenue", value: "+$620K" },
        { label: "Margin Back", value: "38%" },
        { label: "Store CVR", value: "4.4%" },
      ],
      tags: ["Shopify Plus", "CRO", "Speed"],
      image:
        "https://cdn.shopify.com/s/files/1/0644/4086/0781/files/kpxpyi9h0wjo2quodx0m.webp?v=1771923144&width=600",
    },
    {
      id: 5,
      slug: "from-2-failed-builds-to-pixel-perfect-in-10-days",
      service: "Shopify Development",
      category: "Figma to Shopify",
      title: "From 2 Failed Builds to Pixel-Perfect in 10 Days",
      client: "Lumisoft Beauty",
      industry: "Beauty & Skincare",
      metrics: [
        { label: "Figma Accuracy", value: "100%" },
        { label: "Days to Launch", value: "10" },
        { label: "PageSpeed", value: "91" },
      ],
      tags: ["Figma", "Pixel-Perfect", "Speed"],
      image:
        "https://cdn.shopify.com/s/files/1/0644/4086/0781/files/uknt7ki6tzdmhiznuahh.webp?v=1771923143&width=600",
    },
    {
      id: 6,
      slug: "fifteen-days-11k-revenue-and-zero-inventory",
      service: "Shopify Development",
      category: "Dropshipping",
      title: "15 Days, $11K Revenue, Zero Inventory",
      client: "Nestly",
      industry: "Home & Living",
      metrics: [
        { label: "Conversion", value: "3.7%" },
        { label: "Days to Launch", value: "15" },
        { label: "AOV Value", value: "$67" },
      ],
      tags: ["Shopify", "Dropshipping", "Quick Launch"],
      image:
        "https://cdn.shopify.com/s/files/1/0644/4086/0781/files/Case_Story_Cover_Image.webp?v=1771921515&width=600",
    },
  ];

  const services = [
    { key: "all", label: "All Projects" },
    { key: "Shopify Development", label: "Shopify" },
    { key: "WordPress", label: "WordPress" },
    { key: "Mobile App", label: "Mobile App" },
    { key: "Custom Software", label: "Software" },
    { key: "Data & Analytics", label: "Analytics" },
    { key: "UI/UX Design", label: "UI/UX" },
  ];

  const filtered =
    selectedService === "all"
      ? caseStudies
      : caseStudies.filter((cs) => cs.service === selectedService);

  return (
    <section
      className="caseStory-main"
      style={{ padding: "80px 20px", background: "#f8f9fb" }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header */}
        <FadeIn>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <h2
              style={{
                fontSize: "clamp(28px,4vw,38px)",
                color: "#1a1a1a",
                marginBottom: 18,
                lineHeight: "1.2",
              }}
            >
              Real Results, Not Just Pretty Stores
            </h2>
            <p
              style={{
                fontSize: 15,
                color: "#888",
                maxWidth: 500,
                margin: "0 auto",
              }}
            >
              Every project is measured by revenue impact — not just how it
              looks.
            </p>
          </div>
        </FadeIn>

        {/* Service Filters */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 8,
            marginBottom: 36,
            flexWrap: "wrap",
          }}
        >
          {services.map((s) => (
            <button
              key={s.key}
              onClick={() => setSelectedService(s.key)}
              style={{
                padding: "8px 20px",
                borderRadius: 50,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                border:
                  selectedService === s.key
                    ? "2px solid #2563eb"
                    : "1.5px solid #e5e7eb",
                background: selectedService === s.key ? "#2563eb" : "#fff",
                color: selectedService === s.key ? "#fff" : "#555",
                transition: "all .25s",
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Cards Grid */}
        <div
          className="case-story-grid-wrapper"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 20,
          }}
        >
          {filtered.map((study, i) => (
            <FadeIn key={study.id} delay={i * 0.06}>
              <Link
                to={`/${study.slug}`}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  display: "block",
                }}
              >
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: 16,
                    padding: 24,
                    border: "1px solid #e8eaee",
                    borderLeft: "4px solid #2563eb",
                    transition: "all .3s",
                    cursor: "pointer",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 12px 30px rgba(37,99,235,.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  {/* Top: Image + Info side by side */}
                  <div
                    className="case-story-card-top"
                    style={{ display: "flex", gap: 20, marginBottom: 16 }}
                  >
                    {/* Image */}
                    <div
                      style={{
                        width: 150,
                        height: 105,
                        borderRadius: 10,
                        overflow: "hidden",
                        flexShrink: 0,
                        boxShadow: "0 2px 8px rgba(0,0,0,.08)",
                      }}
                    >
                      <img
                        src={study.image}
                        alt={study.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    {/* Info */}
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 6,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 10,
                            letterSpacing: 1.5,
                            color: "#888",
                            textTransform: "uppercase",
                            fontWeight: 600,
                          }}
                        >
                          {study.industry}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            color: "#2563eb",
                            fontWeight: 600,
                          }}
                        >
                          {study.category}
                        </span>
                      </div>
                      <h3
                        style={{
                          fontSize: 17,
                          fontWeight: 700,
                          color: "#1a1a1a",
                          lineHeight: 1.3,
                          marginBottom: 4,
                        }}
                      >
                        {study.title}
                      </h3>
                      <p style={{ fontSize: 13, color: "#666" }}>
                        {study.client}
                      </p>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div
                    className="case-story-card-metrics"
                    style={{ display: "flex", gap: 12, marginBottom: 14 }}
                  >
                    {study.metrics.map((m, j) => (
                      <div
                        key={j}
                        style={{
                          flex: 1,
                          background: "#f0f4ff",
                          borderRadius: 10,
                          padding: "12px 10px",
                          textAlign: "center",
                          border: "1px solid #dbeafe",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "clamp(16px,2vw,20px)",
                            fontWeight: 800,
                            color: "#2563eb",
                          }}
                        >
                          {m.value}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "#555",
                            marginTop: 2,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          {m.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Tags + CTA */}
                  <div
                    className="case-story-card-bottom"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "auto",
                    }}
                  >
                    <div style={{ display: "flex", gap: 6 }}>
                      {study.tags.map((tag, k) => (
                        <span
                          key={k}
                          style={{
                            fontSize: 11,
                            padding: "3px 10px",
                            borderRadius: 50,
                            border: "1px solid #dbeafe",
                            color: "#2563eb",
                            background: "#f0f4ff",
                            fontWeight: 500,
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#2563eb",
                      }}
                    >
                      View Case Study →
                    </span>
                  </div>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>

        {/* No results */}
        {filtered.length === 0 && (
          <div
            style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}
          >
            <p style={{ fontSize: 16, marginBottom: 8 }}>
              No case studies yet for this service.
            </p>
            <p style={{ fontSize: 13 }}>
              We're adding new case studies regularly. Check back soon!
            </p>
          </div>
        )}

        {/* More Case Stories Button */}
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 48 }}
        >
          <Link
            to="/case-story"
            className="srv-btn"
            style={{
              background: "linear-gradient(135deg, #2563eb 0%, #1e3c72 100%)",
            }}
          >
            <span className="srv-btn-text">More Case Stories</span>
            <span className="srv-btn-icon">
              <svg viewBox="0 0 20 20" fill="none">
                <path
                  d="M4 10h12M11 5l5 5-5 5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="srv-btn-shine" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CaseStoryGrid;
